import React, { useState } from 'react';
import { ToleranceList } from './ToleranceList';
import { ToleranceForm } from './ToleranceForm';
import { BookIcon, ChevronRightIcon, ScaleIcon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { RoundingRule } from '../rounding/RoundingConfiguration';
export type ToleranceScope = 'Global' | 'Vendor' | 'OrgUnit';
export type ToleranceRule = {
  id: string;
  toleranceType: 'Amount' | 'Percentage' | 'Combined';
  value: number;
  percentageValue?: number;
  maxAmount?: number;
  roundingId: string | null;
  roundingName: string | null;
  scope: ToleranceScope;
  scopeId: string | null;
  scopeName: string | null;
  scopePath?: string | null;
  modules: string[]; // 'All', 'Invoice', 'PO', 'Budget', etc.
  active: boolean;
  createdAt: string;
  updatedAt: string;
};
export function ToleranceConfiguration({
  locale = 'en-US'
}) {
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editingRule, setEditingRule] = useState<ToleranceRule | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toleranceRules, setToleranceRules] = useState<ToleranceRule[]>([{
    id: 'tol-001',
    toleranceType: 'Amount',
    value: 5.0,
    roundingId: 'round-001',
    roundingName: 'Standard Cents',
    scope: 'Global',
    scopeId: null,
    scopeName: null,
    modules: ['All'],
    active: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'tol-002',
    toleranceType: 'Percentage',
    value: 2.0,
    roundingId: 'round-002',
    roundingName: 'Nickel Rounding',
    scope: 'Vendor',
    scopeId: 'vendor-001',
    scopeName: 'Office Depot',
    modules: ['Invoice', 'PO'],
    active: true,
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2023-01-15T00:00:00Z'
  }, {
    id: 'tol-003',
    toleranceType: 'Amount',
    value: 10.0,
    roundingId: null,
    roundingName: null,
    scope: 'OrgUnit',
    scopeId: 'ou-apac-sg',
    scopeName: 'Singapore Office',
    scopePath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    modules: ['Budget'],
    active: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }, {
    id: 'tol-004',
    toleranceType: 'Percentage',
    value: 5.0,
    roundingId: 'round-003',
    roundingName: 'Whole Dollar',
    scope: 'OrgUnit',
    scopeId: 'ou-latam-mx',
    scopeName: 'Mexico Office',
    scopePath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    modules: ['Invoice'],
    active: false,
    createdAt: '2023-02-15T00:00:00Z',
    updatedAt: '2023-03-01T00:00:00Z'
  }, {
    id: 'tol-005',
    toleranceType: 'Combined',
    percentageValue: 3.0,
    maxAmount: 500.0,
    value: 0,
    roundingId: 'round-001',
    roundingName: 'Standard Cents',
    scope: 'Global',
    scopeId: null,
    scopeName: null,
    modules: ['Invoice', 'PO'],
    active: true,
    createdAt: '2023-03-10T00:00:00Z',
    updatedAt: '2023-03-10T00:00:00Z'
  }]);
  const [roundingRules, setRoundingRules] = useState<RoundingRule[]>([{
    id: 'round-001',
    name: 'Standard Cents',
    description: 'Round to nearest cent (0.01)',
    roundingType: 'Nearest',
    interval: 0.01,
    scope: 'Tenant',
    active: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'round-002',
    name: 'Nickel Rounding',
    description: 'Round up to nearest 0.05',
    roundingType: 'Up',
    interval: 0.05,
    scope: 'Tenant',
    active: true,
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2023-01-15T00:00:00Z'
  }, {
    id: 'round-003',
    name: 'Whole Dollar',
    description: 'Round down to nearest dollar',
    roundingType: 'Down',
    interval: 1.0,
    scope: 'Tenant',
    active: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }]);
  const handleAddRule = () => {
    setEditingRule(null);
    setView('form');
  };
  const handleEditRule = (rule: ToleranceRule) => {
    setEditingRule(rule);
    setView('form');
  };
  const handleSaveRule = (rule: Omit<ToleranceRule, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingRule) {
      // Update existing rule
      setToleranceRules(toleranceRules.map(r => r.id === editingRule.id ? {
        ...editingRule,
        ...rule,
        updatedAt: new Date().toISOString()
      } : r));
    } else {
      // Create new rule
      const newRule: ToleranceRule = {
        id: `tol-${Date.now()}`,
        ...rule,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setToleranceRules([...toleranceRules, newRule]);
    }
    setView('list');
    setEditingRule(null);
  };
  const handleToggleActive = (id: string) => {
    setToleranceRules(toleranceRules.map(rule => rule.id === id ? {
      ...rule,
      active: !rule.active,
      updatedAt: new Date().toISOString()
    } : rule));
  };
  const handleDeleteRule = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setToleranceRules(toleranceRules.filter(rule => rule.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleCancelForm = () => {
    setView('list');
    setEditingRule(null);
  };
  const formatToleranceValue = (rule: ToleranceRule) => {
    if (rule.toleranceType === 'Amount') {
      return `$${rule.value.toFixed(2)}`;
    } else if (rule.toleranceType === 'Percentage') {
      return `${rule.value}%`;
    } else {
      return `${rule.percentageValue}% (max $${rule.maxAmount?.toFixed(2)})`;
    }
  };
  const globalRules = toleranceRules.filter(r => r.scope === 'Global');
  const vendorRules = toleranceRules.filter(r => r.scope === 'Vendor');
  const orgUnitRules = toleranceRules.filter(r => r.scope === 'OrgUnit');
  return <div data-id="element-12757">
      <div className="mb-8" data-id="element-12758">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-12759">
          Tolerance Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-12760">
          Define acceptable deviation when matching values across global,
          vendor, and organisation unit levels
        </p>
      </div>

      {view === 'list' ? <>
          {/* Global Rules */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-12761">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12762">
              <div className="flex items-center justify-between" data-id="element-12763">
                <div data-id="element-12764">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12765">
                    Global Tolerance Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-12766">
                    Default tolerance rules applied across the entire system
                  </p>
                </div>
                <button onClick={handleAddRule} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12767">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-12768" />
                  Add Rule
                </button>
              </div>
            </div>

            {globalRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-12769">
                <p className="text-sm text-gray-500" data-id="element-12770">
                  No global tolerance rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-12771">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-12772">
                  <thead className="bg-gray-50" data-id="element-12773">
                    <tr data-id="element-12774">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12775">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12776">
                        Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12777">
                        Modules
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12778">
                        Rounding
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12779">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12780">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-12781">
                    {globalRules.map(rule => <tr key={rule.id} data-id="element-12782">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-12783">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-12784">
                            {rule.toleranceType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-12785">
                          {formatToleranceValue(rule)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12786">
                          {rule.modules.join(', ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12787">
                          {rule.roundingName || 'None'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-12788">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-12789">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-12790" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-12791">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12792">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-12793">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-12794">
                            <EditIcon className="h-4 w-4 inline" data-id="element-12795" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-12796">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-12797" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* OrgUnit-Specific Rules */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-12798">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12799">
              <div className="flex items-center justify-between" data-id="element-12800">
                <div data-id="element-12801">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12802">
                    Organisation Unit Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-12803">
                    Tolerance rules for specific organisation units
                  </p>
                </div>
              </div>
            </div>

            {orgUnitRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-12804">
                <p className="text-sm text-gray-500" data-id="element-12805">
                  No organisation unit-specific tolerance rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-12806">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-12807">
                  <thead className="bg-gray-50" data-id="element-12808">
                    <tr data-id="element-12809">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12810">
                        Organisation Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12811">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12812">
                        Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12813">
                        Modules
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12814">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12815">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-12816">
                    {orgUnitRules.map(rule => <tr key={rule.id} data-id="element-12817">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-12818">
                          <div className="text-sm font-medium text-gray-900" data-id="element-12819">
                            {rule.scopeName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-12820">
                            {rule.scopePath}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-12821">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-12822">
                            {rule.toleranceType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-12823">
                          {formatToleranceValue(rule)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12824">
                          {rule.modules.join(', ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-12825">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-12826">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-12827" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-12828">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12829">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-12830">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-12831">
                            <EditIcon className="h-4 w-4 inline" data-id="element-12832" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-12833">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-12834" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Vendor-Specific Rules */}
          <div className="bg-white shadow rounded-lg" data-id="element-12835">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12836">
              <div className="flex items-center justify-between" data-id="element-12837">
                <div data-id="element-12838">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12839">
                    Vendor-Specific Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-12840">
                    Tolerance rules for specific vendors
                  </p>
                </div>
              </div>
            </div>

            {vendorRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-12841">
                <p className="text-sm text-gray-500" data-id="element-12842">
                  No vendor-specific tolerance rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-12843">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-12844">
                  <thead className="bg-gray-50" data-id="element-12845">
                    <tr data-id="element-12846">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12847">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12848">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12849">
                        Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12850">
                        Modules
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12851">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12852">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-12853">
                    {vendorRules.map(rule => <tr key={rule.id} data-id="element-12854">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-12855">
                          {rule.scopeName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-12856">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-12857">
                            {rule.toleranceType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-12858">
                          {formatToleranceValue(rule)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12859">
                          {rule.modules.join(', ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-12860">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-12861">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-12862" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-12863">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12864">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-12865">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-12866">
                            <EditIcon className="h-4 w-4 inline" data-id="element-12867" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-12868">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-12869" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </> : <ToleranceForm rule={editingRule} roundingRules={roundingRules.filter(rule => rule.active)} onSave={handleSaveRule} onCancel={handleCancelForm} locale={locale} data-id="element-12870" />}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-12871">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-12872">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-12873">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-12874"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-12875">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-12876">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-12877">
                <div className="sm:flex sm:items-start" data-id="element-12878">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-12879">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-12880" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-12881">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12882">
                      Delete Tolerance Rule
                    </h3>
                    <div className="mt-2" data-id="element-12883">
                      <p className="text-sm text-gray-500" data-id="element-12884">
                        Are you sure you want to delete this tolerance rule?
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-12885">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-12886">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-12887">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}